import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Trophy,
  Timer,
  Wind,
  Zap,
  Footprints,
  Sparkles,
  Frown,
  Meh,
  Smile,
  Angry,
  BatteryLow,
  ChevronRight,
  AlertTriangle,
  RefreshCcw,
  Play,
  Gauge,
  Youtube,
  Moon,
  Utensils,
  Pill,
  Droplet,
  BookOpen,
  ExternalLink,
  CheckCircle2,
  Circle,
  History,
  ClipboardList,
  Settings as SettingsIcon,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API } from '@/utils/constants';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import { useAuth } from '@/contexts/AuthContext';
import InstallAppButton from '@/components/pwa/InstallAppButton';
import { useIsPwaMode } from '@/hooks/useIsPwaMode';
import Navigation from '@/components/Navigation';

/* eslint-disable react/no-unescaped-entities */

const C = {
  primary: '#0EA5E9',
  primaryDark: '#0284C7',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
  cloud: '#F8FAFC',
  skyPale: '#F0F9FF',
  skySoft: '#E0F2FE',
  border: '#E2E8F0',
  white: '#FFFFFF',
  amber: '#D97706',
  amberBg: '#FFFBEB',
  amberBorder: '#FCD34D',
  danger: '#DC2626',
  dangerBg: '#FEF2F2',
  dangerBorder: '#FCA5A5',
};

const SectionLabel = ({ number, children }) => (
  <div className="flex items-center gap-3 mb-4">
    {number != null && (
      <span
        className="inline-flex items-center justify-center rounded-full text-xs font-semibold"
        style={{ width: 28, height: 28, backgroundColor: C.skySoft, color: C.primaryDark }}
      >
        {number}
      </span>
    )}
    <span
      className="text-xs sm:text-sm uppercase tracking-[0.22em]"
      style={{ color: C.primaryDark, fontWeight: 500 }}
    >
      {children}
    </span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2
    className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6"
    style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
  >
    {children}
  </h2>
);

// -----------------------------------------------------------------------------
// Feelings & self-check definitions
// -----------------------------------------------------------------------------
const FEELINGS = [
  { id: 'happy', label: 'Heureux, plein d’énergie', icon: Smile, color: '#F59E0B' },
  { id: 'relaxed', label: 'Détendu', icon: Meh, color: '#10B981' },
  { id: 'stressed', label: 'Stressé', icon: Frown, color: '#EF4444' },
  { id: 'angry', label: 'En colère', icon: Angry, color: '#DC2626' },
  { id: 'unmotivated', label: 'Démotivé', icon: BatteryLow, color: '#6366F1' },
];

// Each question: id, prompt, and the shape of answer (positive=healthy)
const CHECK_QUESTIONS = [
  {
    id: 'mouth',
    prompt: 'Je respire actuellement par…',
    options: [
      { value: 'nose', label: 'Le nez', good: true },
      { value: 'mouth', label: 'La bouche', good: false },
      { value: 'both', label: 'Un peu des deux', good: false },
    ],
  },
  {
    id: 'neck',
    prompt: 'Est-ce que je ressens des tensions dans le cou et les épaules à chaque inspiration ?',
    options: [
      { value: 'no', label: 'Non, épaules détendues', good: true },
      { value: 'yes', label: 'Oui, elles se soulèvent ou sont tendues', good: false },
    ],
  },
  {
    id: 'belly',
    prompt: 'Mon ventre est-il relâché et se remplit-il à l’inspiration puis se dégonfle-t-il à l’expiration ?',
    options: [
      { value: 'yes', label: 'Oui, mouvement souple et complet', good: true },
      { value: 'no', label: 'Non, il est bloqué ou immobile', good: false },
    ],
  },
  {
    id: 'inhale_quality',
    prompt: 'L’inspiration est-elle douce, lente et progressive ?',
    options: [
      { value: 'yes', label: 'Oui', good: true },
      { value: 'no', label: 'Non, elle est courte, brusque ou saccadée', good: false },
    ],
  },
  {
    id: 'exhale_complete',
    prompt: 'L’expiration est-elle complète, avec un relâchement des muscles respiratoires ?',
    options: [
      { value: 'yes', label: 'Oui, complète et relâchée', good: true },
      { value: 'no', label: 'Non, elle est brève, forcée ou incomplète', good: false },
    ],
  },
  {
    id: 'rate',
    prompt: 'Mon rythme respiratoire est actuellement…',
    options: [
      { value: 'calm', label: 'Calme (≈ 10-14 cycles / min)', good: true },
      { value: 'fast', label: 'Rapide', good: false },
      { value: 'irregular', label: 'Irrégulier', good: false },
    ],
  },
  {
    id: 'pause',
    prompt: 'Y a-t-il une petite pause naturelle après chaque expiration ?',
    options: [
      { value: 'yes', label: 'Oui, une pause détendue', good: true },
      { value: 'no', label: 'Non, l’inspiration suivante enchaîne aussitôt', good: false },
    ],
  },
  {
    id: 'air_hunger',
    prompt: 'Est-ce que je ressens une sensation de manque d’air, de faim d’air ?',
    options: [
      { value: 'no', label: 'Non', good: true },
      { value: 'yes', label: 'Oui, une gêne / envie de « prendre plus d’air »', good: false },
    ],
  },
  {
    id: 'lower_ribs',
    prompt: 'Est-ce que je sens mes côtes du bas s’écarter à l’inspiration et se resserrer à l’expiration ?',
    options: [
      { value: 'yes', label: 'Oui, mouvement latéral perceptible', good: true },
      { value: 'no', label: 'Non, je ne sens rien bouger dans le bas des côtes', good: false },
    ],
  },
  {
    id: 'block',
    prompt: 'Y a-t-il une sensation de blocage à l’inspiration ou à l’expiration ?',
    options: [
      { value: 'none', label: 'Aucun blocage', good: true },
      { value: 'inhale', label: 'Un blocage à l’inspiration', good: false },
      { value: 'exhale', label: 'Un blocage à l’expiration', good: false },
      { value: 'both', label: 'Les deux', good: false },
    ],
  },
];

// -----------------------------------------------------------------------------
// YouTube library (vidéos de la chaîne @Jaccardjphysio)
// -----------------------------------------------------------------------------
const YT_VIDEOS = {
  shoulders_abdo: {
    id: 'q4YCmb4SAGo',
    title: 'Technique pour désengager les épaules et ressentir la respiration abdominale',
    duration: '3:03',
  },
  optimal_mechanics: {
    id: 'WAJkNpz1Lg0',
    title: 'La mécanique respiratoire optimale',
    duration: '3:32',
  },
  co2_tolerance: {
    id: 'O61hmtfGXfI',
    title: 'Améliorer sa tolérance au CO₂ à l’effort : exercice 1',
    duration: '4:18',
  },
  breath_mobility: {
    id: 'hfxVOI_WjYU',
    title: 'Utiliser la respiration pour améliorer sa mobilité',
    duration: '3:41',
  },
};
const ytUrl = (id) => `https://www.youtube.com/watch?v=${id}`;
const ytThumb = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;

// -----------------------------------------------------------------------------
// Coaching blocks — richer step-by-step protocols shown alongside exercise cards.
// Each block has a title, an intro, an "if easy" note, and an ordered fallback list
// with sub-steps when the patient struggles with the target sensation.
// -----------------------------------------------------------------------------
const COACHING_BLOCKS = {
  mouth: {
    id: 'mouth-to-nose',
    title: 'Retrouver une respiration nasale — protocole pas à pas',
    intro: 'Essayez de respirer pendant 1 minute uniquement par le nez, inspiration ET expiration.',
    ifEasyLabel: 'Si c\u2019est facile',
    ifEasyText: 'Continuez à privilégier la respiration nasale le plus possible au quotidien (marche, bureau, sommeil), jusqu\u2019à ce que cela devienne automatique.',
    ifHardLabel: 'Si c\u2019est difficile ou impossible',
    ifHardIntro: 'Ne forcez pas — enchaînez ces étapes dans l\u2019ordre :',
    ifHardSteps: [
      {
        title: 'Identifier la cause',
        body: 'Cherchez la/les cause(s) possible(s) parmi les 3 types de stresseurs : physique (posture, effort, nez bouché), chimique (alimentation, alcool, tabac, hydratation), psychologique (stress, émotion, hypervigilance).',
      },
      {
        title: 'Rehausser le CO₂',
        body: 'Respirez quelques minutes dans vos mains posées en coupe contre le visage (nez et bouche couverts). Cela fait remonter le CO₂ et apaise la sensation de faim d\u2019air.',
      },
      {
        title: 'Choisir l\u2019exercice selon la cause',
        body: 'Deux options selon ce que vous ressentez : les « petites pauses répétées » (Buteyko) ou la « réduction de la fréquence respiratoire ». Cartes ci-dessous.',
      },
      {
        title: 'Percevoir le mouvement des côtes basses',
        body: 'Posez les mains sur les côtes du bas (sur les côtés). Essayez de sentir les côtes s\u2019écarter à l\u2019inspiration et se resserrer à l\u2019expiration. Sans forcer.',
      },
      {
        title: 'Respiration diaphragmatique en position couchée',
        body: 'Allongé sur le dos, genoux repliés, pieds à plat. Une main sur le ventre, une main sur la poitrine. À l\u2019inspiration, sentez le ventre se gonfler doucement. À l\u2019expiration, sentez tout se relâcher. Cherchez à allonger progressivement le temps d\u2019expiration.',
      },
    ],
  },
  neck: {
    id: 'neck-shoulders-safety',
    title: 'Épaules tendues, respiration haute : ramener de la sécurité dans le corps',
    intro: 'La respiration dans les épaules, la partie supérieure de la poitrine et les muscles du cou est caractéristique d’une activation aiguë ou chronique du système nerveux de stress (sympathique) — le corps se sent en insécurité.',
    steps: [
      {
        title: '1ᵉʳ temps — Porter l’attention sur la tension',
        body: 'Ressentez la tension : quelle est exactement la sensation ? Parcourez lentement les trapèzes, les épaules, les bras, les coudes, les avant-bras, les mains et les doigts. Ne refusez pas et ne luttez pas contre la sensation : si elle est présente, c’est que le corps en a besoin. Souvenez-vous : le corps a raison, il ne dysfonctionne pas — il réagit et s’adapte à des stresseurs (mécaniques, chimiques ou psychologiques, du présent ou du passé). Vous pouvez essayer de lister ces stresseurs pour les identifier ; si c’est impossible, restez simplement sur la sensation et comprenez-la. Pensez à ces deux réactions face à une personne : si vous l’écoutez, la comprenez et la respectez, comment réagira-t-elle ? Et si vous faites l’opposé, quelle sera son attitude ? Le corps répond de la même manière.',
      },
      {
        title: '2ᵉ temps — Ramener un sentiment de sécurité dans le corps',
        body: 'Choisissez une ou plusieurs de ces approches : éducation somatique de Hanna ; tremblements thérapeutiques, Zen Swing, Posture de l’arbre (apprises en séance) ; techniques d’activation du nerf vague — humming, exercice de base de Rosenfeld, eau froide sur le visage, socialisation si possible ; danse, taï-chi.',
      },
      {
        title: '3ᵉ temps — Se reconnecter à une respiration diaphragmatique',
        body: 'Une fois le sentiment de sécurité rétabli, revenez doucement à une respiration nasale, silencieuse, ample dans le bas des côtes et l’abdomen — voir les cartes d’exercices ci-dessous (Cohérence 5-5, Calming Breath, réduction de la fréquence). Ne forcez rien : laissez la respiration se ré-organiser d’elle-même.',
      },
    ],
  },
  relax: {
    id: 'parasympathetic-activation',
    title: 'Activer le système nerveux de relaxation (parasympathique)',
    intro: 'Objectif : envoyer au cerveau un signal clair de sécurité pour faire redescendre la tension. Enchaînez 2 à 3 de ces techniques à votre rythme.',
    steps: [
      {
        title: 'Humming (chantonnement)',
        body: 'Bouche fermée, expiration longue en produisant un doux « mmm… ». La vibration stimule le nerf vague et allonge naturellement l\u2019expiration. 5 à 10 humming lents.',
      },
      {
        title: 'Exercice de base de Rosenfeld',
        body: 'Assis(e), colonne allongée, épaules relâchées. Prenez conscience du contact avec le sol, du souffle qui entre et sort par le nez. Laissez la respiration se ralentir d\u2019elle-même, sans effort. 2 à 3 minutes.',
      },
      {
        title: 'Focus sur l\u2019expiration',
        body: 'À chaque cycle, portez toute votre attention sur l\u2019expiration. Laissez-la être plus longue que l\u2019inspiration (par ex. inspir 4 s / expir 6 s). N\u2019oubliez pas la petite pause détendue à la fin.',
      },
      {
        title: 'Inspirations lentes, douces, progressives',
        body: 'Focalisez-vous sur des inspirations légères, silencieuses, qui montent progressivement du bas des côtes vers le haut. Aucune brusquerie ni « grande » inspiration.',
      },
      {
        title: 'Double soupir (physiological sigh)',
        body: 'Deux inspirations nasales successives (une longue puis une courte pour finir de remplir), suivies d\u2019une longue expiration par la bouche. Répétez 2 à 3 fois. Effet apaisant quasi immédiat, particulièrement utile en cas de pic de stress.',
      },
    ],
  },
};

// -----------------------------------------------------------------------------
// Program library — every exercise the /mon-espace page can recommend
// -----------------------------------------------------------------------------
const PROGRAM_LIB = {
  // PRIORITY exercises for "manque d'air" -> BOLT + petites apnées
  bolt: {
    title: 'Mesurer votre BOLT',
    desc: 'Établissez votre score de tolérance au CO₂ pour objectiver votre niveau de départ.',
    href: '/breathing/challenges-intro',
    icon: Gauge,
    priority: true,
  },
  petites_pauses: {
    title: 'Petites apnées répétées',
    desc: 'Rétentions courtes après expiration pour réduire la sur-ventilation (Exercice 1 du protocole).',
    href: '/breathing/practice?mode=guided&exercise=petites-pauses',
    icon: Timer,
    priority: true,
  },
  // Standard exercises
  buteyko: {
    title: 'Respiration Buteyko',
    desc: 'Respiration nasale douce, réduction de la ventilation excessive.',
    href: '/breathing/practice?mode=guided&exercise=buteyko',
    icon: Wind,
  },
  coherence: {
    title: 'Cohérence cardiaque 5-5',
    desc: 'Inspiration 5 s, expiration 5 s — équilibre le système nerveux.',
    href: '/breathing/practice?mode=guided&exercise=coherence',
    icon: Sparkles,
  },
  technique478: {
    title: 'Technique 4-7-8',
    desc: 'Inspirer 4 s, retenir 7 s, expirer 8 s — apaise en profondeur.',
    href: '/breathing/practice?mode=guided&exercise=technique-4-7-8',
    icon: Sparkles,
  },
  box: {
    title: 'Respiration carrée (Box)',
    desc: '4-4-4-4 — recentre l’attention et le calme.',
    href: '/breathing/practice?mode=guided&exercise=box',
    icon: Sparkles,
  },
  calming: {
    title: 'Calming Breath',
    desc: 'Expiration allongée qui détend cou, épaules et diaphragme.',
    href: '/breathing/practice?mode=guided&exercise=calming',
    icon: Wind,
  },
  reduce_freq: {
    title: 'Réduire la fréquence',
    desc: 'Ralentir progressivement le rythme respiratoire.',
    href: '/breathing/practice?mode=guided&exercise=reduire-frequence',
    icon: Timer,
  },
  pursed_lip: {
    title: 'Focus sur la capacité à expirer : Allongement du temps expiratoire',
    desc: 'Expirez lentement par la bouche, lèvres à peine entrouvertes (comme pour souffler sur une bougie). Détend et allonge l’expiration.',
    href: '/breathing/practice?mode=guided&exercise=pursed-lip',
    icon: Wind,
  },
  bellows: {
    title: 'Souffle du soufflet (Bellows)',
    desc: 'Respiration énergisante à intensité contrôlée.',
    href: '/breathing/practice?mode=guided&exercise=bellows',
    icon: Zap,
  },
  wim_hof: {
    title: 'Méthode Wim Hof',
    desc: 'Cycles d’hyperventilation contrôlée + apnée pour l’énergie et la vitalité.',
    href: '/breathing/practice?mode=guided&exercise=wim-hof',
    icon: Zap,
  },
  alternate: {
    title: 'Respiration alternée (Nadi Shodhana)',
    desc: 'Alternance narine gauche/droite — apaise, équilibre.',
    href: '/breathing/practice?mode=guided&exercise=alternate-nostril',
    icon: Wind,
  },
  oxygen_advantage: {
    title: 'Protocole Oxygen Advantage',
    desc: 'Programme complet pour l’hyperventilation, à faire sur plusieurs semaines.',
    href: '/breathing/oxygen-advantage',
    icon: Trophy,
  },
};

// -----------------------------------------------------------------------------
// Recommendation engine
// -----------------------------------------------------------------------------
function buildProgram(feeling, answers) {
  const picked = new Set();
  const reasons = [];
  const videos = new Map(); // key -> { video, reason }
  const blocks = new Map(); // key -> coaching block (from COACHING_BLOCKS)
  const addVideo = (key, reason) => {
    if (!videos.has(key)) videos.set(key, { video: YT_VIDEOS[key], reason });
  };
  const addBlock = (key) => {
    if (!blocks.has(key) && COACHING_BLOCKS[key]) blocks.set(key, COACHING_BLOCKS[key]);
  };

  // ALWAYS include cohérence cardiaque — the most powerful practice to
  // rebalance the nervous system and support homeostasis.
  picked.add('coherence');
  reasons.push({
    key: 'coherence-always',
    text: 'Cohérence cardiaque 5-5 systématiquement recommandée : c’est la pratique la plus puissante pour rééquilibrer le système nerveux et soutenir l’homéostasie.',
  });

  // ABSOLUTE RULE: sensation de manque d'air → BOLT + petites apnées répétées
  const hasAirHunger = answers.air_hunger === 'yes';
  if (hasAirHunger) {
    picked.add('bolt');
    picked.add('petites_pauses');
    reasons.push({
      key: 'air_hunger',
      text: 'Vous ressentez une gêne / faim d’air : nous vous recommandons de d’abord mesurer votre BOLT, puis de pratiquer les « petites apnées répétées ». C’est le point de départ prioritaire.',
      critical: true,
    });
    addVideo('co2_tolerance', 'Vidéo explicative de l’exercice « petites apnées répétées » pour améliorer la tolérance au CO₂.');
  }

  // Emotion-based additions
  switch (feeling) {
    case 'happy':
      picked.add('wim_hof');
      picked.add('bellows');
      reasons.push({ key: 'happy', text: 'Bonne énergie du jour : profitez-en pour un exercice tonifiant (Wim Hof ou Bellows).' });
      break;
    case 'relaxed':
      picked.add('coherence');
      picked.add('buteyko');
      reasons.push({ key: 'relaxed', text: 'Vous êtes déjà bien : consolidez avec la cohérence cardiaque et la respiration Buteyko.' });
      break;
    case 'stressed':
      picked.add('technique478');
      picked.add('coherence');
      picked.add('calming');
      reasons.push({ key: 'stressed', text: 'Objectif : faire redescendre le système nerveux. Cohérence 5-5, 4-7-8 et Calming Breath.' });
      addBlock('relax');
      break;
    case 'angry':
      picked.add('alternate');
      picked.add('technique478');
      picked.add('coherence');
      reasons.push({ key: 'angry', text: 'Colère : la respiration alternée et le 4-7-8 rééquilibrent rapidement les deux hémisphères.' });
      addBlock('relax');
      break;
    case 'unmotivated':
      picked.add('bellows');
      picked.add('wim_hof');
      reasons.push({ key: 'unmotivated', text: 'Baisse de motivation : un exercice énergisant (Bellows ou Wim Hof) pour relancer la machine.' });
      break;
    default:
      break;
  }

  // Symptom-based additions
  if (answers.mouth === 'mouth' || answers.mouth === 'both') {
    picked.add('buteyko');
    reasons.push({ key: 'mouth', text: 'Vous respirez par la bouche : Buteyko pour réhabituer la respiration nasale.' });
    addBlock('mouth');
  }
  if (answers.neck === 'yes') {
    picked.add('calming');
    picked.add('pursed_lip');
    reasons.push({ key: 'neck', text: 'Tensions cou/épaules : Calming Breath + expiration lèvres pincées pour relâcher les muscles inspirateurs accessoires.' });
    addBlock('neck');
    addVideo('shoulders_abdo', 'Désengager les épaules et ressentir la respiration abdominale.');
  }
  if (answers.belly === 'no') {
    picked.add('reduce_freq');
    picked.add('calming');
    picked.add('pursed_lip');
    reasons.push({ key: 'belly', text: 'Ventre peu mobile : ralentissez la fréquence, pratiquez la respiration diaphragmatique + expiration lèvres pincées pour libérer le diaphragme.' });
    addVideo('shoulders_abdo', 'Retrouver un ventre qui bouge à chaque respiration.');
    addVideo('optimal_mechanics', 'Comprendre la mécanique respiratoire optimale (diaphragme et abdomen).');
  }
  if (answers.inhale_quality === 'no') {
    picked.add('reduce_freq');
    reasons.push({ key: 'inhale', text: 'Inspiration saccadée : ralentissement + cohérence 5-5 remettent de la fluidité.' });
  }
  if (answers.exhale_complete === 'no') {
    picked.add('technique478');
    picked.add('calming');
    picked.add('pursed_lip');
    reasons.push({ key: 'exhale', text: 'Expiration incomplète : le 4-7-8, la Calming Breath et surtout l’expiration lèvres pincées allongent et détendent la sortie d’air.' });
    addVideo('optimal_mechanics', 'Voir comment se déroule une expiration complète, muscles relâchés.');
  }
  if (answers.rate === 'fast' || answers.rate === 'irregular') {
    picked.add('reduce_freq');
    picked.add('pursed_lip');
    reasons.push({ key: 'rate', text: 'Rythme rapide/irrégulier : cohérence 5-5 pour poser 6 cycles/min, réduction de la fréquence et expiration lèvres pincées pour ralentir sûrement.' });
  }
  if (answers.pause === 'no') {
    picked.add('petites_pauses');
    picked.add('buteyko');
    reasons.push({ key: 'pause', text: 'Pas de pause après expiration : les petites apnées répétées installent la pause détendue.' });
  }
  if (answers.lower_ribs === 'no') {
    picked.add('reduce_freq');
    reasons.push({ key: 'ribs', text: 'Côtes basses peu mobiles : travail diaphragmatique et respiration lente guidée + cohérence 5-5.' });
    addVideo('optimal_mechanics', 'Visualiser l’écartement latéral des côtes basses à l’inspiration.');
    addVideo('breath_mobility', 'Utiliser la respiration pour améliorer la mobilité thoracique.');
  }
  if (answers.block === 'inhale' || answers.block === 'both') {
    picked.add('pursed_lip');
    reasons.push({ key: 'block_in', text: 'Blocage à l’inspiration : cohérence 5-5 + expiration lèvres pincées pour ré-ouvrir la porte d’entrée en douceur.' });
    addVideo('optimal_mechanics', 'Lever le blocage en retrouvant la mécanique naturelle.');
  }
  if (answers.block === 'exhale' || answers.block === 'both') {
    picked.add('technique478');
    picked.add('pursed_lip');
    reasons.push({ key: 'block_out', text: 'Blocage à l’expiration : le 4-7-8 et surtout l’expiration lèvres pincées rétablissent une expiration longue et fluide.' });
    addVideo('optimal_mechanics', 'Lever le blocage en retrouvant la mécanique naturelle.');
  }

  // If several dysfunctional signals → add Oxygen Advantage as a broader program
  const badCount = CHECK_QUESTIONS.filter((q) => {
    const opt = q.options.find((o) => o.value === answers[q.id]);
    return opt && opt.good === false;
  }).length;
  if (badCount >= 4 && !hasAirHunger) {
    picked.add('oxygen_advantage');
    reasons.push({
      key: 'many_signs',
      text: `${badCount} signes de respiration dysfonctionnelle détectés : envisagez le protocole complet Oxygen Advantage sur plusieurs semaines.`,
    });
  }

  // Turn set into array with priority items first
  const list = Array.from(picked).map((k) => ({ key: k, ...PROGRAM_LIB[k] }));
  list.sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));
  return {
    exercises: list,
    reasons,
    videos: Array.from(videos.entries()).map(([key, v]) => ({ key, ...v })),
    blocks: Array.from(blocks.values()),
  };
}

// LocalStorage keys — daily reset so patients start fresh each day
const STORAGE_KEY = 'mon-espace:selfcheck:v1';
const DONE_KEY_PREFIX = 'mon-espace:done:'; // + YYYY-MM-DD
const HISTORY_KEY = 'mon-espace:history:v1';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const loadDone = () => {
  try {
    const raw = localStorage.getItem(DONE_KEY_PREFIX + todayISO());
    if (!raw) return { exercises: [], videos: [] };
    const p = JSON.parse(raw);
    return { exercises: p.exercises || [], videos: p.videos || [] };
  } catch (e) { return { exercises: [], videos: [] }; }
};
const saveDone = (state) => {
  try { localStorage.setItem(DONE_KEY_PREFIX + todayISO(), JSON.stringify(state)); } catch (e) { /* ignore */ }
};
const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) { return []; }
};
const saveHistory = (list) => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 60))); } catch (e) { /* ignore */ }
};
const isSameDay = (isoDate) => {
  if (!isoDate) return false;
  const d = new Date(isoDate);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};
const loadStoredCheck = () => {
  if (typeof window === 'undefined') return { feeling: null, answers: {} };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { feeling: null, answers: {} };
    const parsed = JSON.parse(raw);
    if (!isSameDay(parsed.savedAt)) return { feeling: null, answers: {} };
    return { feeling: parsed.feeling ?? null, answers: parsed.answers ?? {} };
  } catch (e) {
    return { feeling: null, answers: {} };
  }
};

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
const MonEspacePage = () => {
  const { user } = useAuth();
  const isPwa = useIsPwaMode();

  const [records, setRecords] = useState(null);
  const [challengeScores, setChallengeScores] = useState([]);
  const [wimHofBest, setWimHofBest] = useState(null); // { retention: number, date: string }
  const [loading, setLoading] = useState(true);

  const stored = useMemo(() => loadStoredCheck(), []);
  const [feeling, setFeeling] = useState(stored.feeling);
  const [answers, setAnswers] = useState(stored.answers);
  const [done, setDone] = useState(loadDone);
  const [history, setHistory] = useState(loadHistory);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isDoneExercise = (k) => done.exercises.includes(k);
  const isDoneVideo = (k) => done.videos.includes(k);
  const toggleDoneExercise = (k) => setDone((d) => {
    const set = new Set(d.exercises);
    if (set.has(k)) set.delete(k); else set.add(k);
    const next = { ...d, exercises: Array.from(set) };
    saveDone(next);
    return next;
  });
  const toggleDoneVideo = (k) => setDone((d) => {
    const set = new Set(d.videos);
    if (set.has(k)) set.delete(k); else set.add(k);
    const next = { ...d, videos: Array.from(set) };
    saveDone(next);
    return next;
  });

  // Auto-save the daily check-in to history on every meaningful change.
  useEffect(() => {
    if (!feeling && Object.keys(answers).length === 0) return;
    const entry = {
      date: todayISO(),
      feeling,
      answers,
      exercisesDone: done.exercises.length,
      videosDone: done.videos.length,
      savedAt: new Date().toISOString(),
    };
    const list = loadHistory().filter((h) => h.date !== entry.date);
    const next = [entry, ...list];
    saveHistory(next);
    setHistory(next);
    // Server sync (best-effort, no UI blocking)
    axios
      .put(`${API}/mon-espace/checkins/${entry.date}`, {
        date: entry.date,
        feeling,
        answers,
        exercises_done: done.exercises,
        videos_done: done.videos,
      })
      .catch(() => { /* offline / server hiccup — local storage keeps everything */ });
  }, [feeling, answers, done]);

  // On mount, hydrate history from the server (merge with local — server wins per date).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/mon-espace/checkins`, { params: { limit: 60 } });
        if (cancelled || !Array.isArray(res.data)) return;
        const serverList = res.data.map((r) => ({
          date: r.date,
          feeling: r.feeling,
          answers: r.answers,
          exercisesDone: (r.exercises_done || []).length,
          videosDone: (r.videos_done || []).length,
          savedAt: r.saved_at,
        }));
        const localOnly = loadHistory().filter((h) => !serverList.some((s) => s.date === h.date));
        const merged = [...serverList, ...localOnly].sort((a, b) => (b.date > a.date ? 1 : -1));
        saveHistory(merged);
        setHistory(merged);
      } catch (e) { /* not signed-in / no data yet — silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const sendSummaryToCabinet = async () => {
    try {
      const res = await axios.post(`${API}/mon-espace/send-summary`, {
        date: todayISO(),
        feeling,
        answers,
        exercises_done: done.exercises,
        videos_done: done.videos,
      });
      if (res.data?.ok) alert('Votre bilan a été envoyé au cabinet. Merci !');
    } catch (e) {
      alert('Erreur envoi : ' + (e.response?.data?.detail || e.message));
    }
  };

  // Persist feeling + answers on every change (for return-after-exercise flow)
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ feeling, answers, savedAt: new Date().toISOString() })
      );
    } catch (e) {
      // ignore quota errors
    }
  }, [feeling, answers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [recRes, chalRes, sessRes] = await Promise.all([
          axios.get(`${API}/challenges/records`),
          axios.get(`${API}/challenges`, { params: { limit: 200 } }),
          axios.get(`${API}/sessions`, { params: { limit: 200 } }),
        ]);
        if (cancelled) return;
        setRecords(recRes.data || {});
        setChallengeScores(Array.isArray(chalRes.data) ? chalRes.data : []);

        // Best Wim Hof retention over all rounds & sessions
        const sessions = Array.isArray(sessRes.data) ? sessRes.data : [];
        let best = null;
        sessions.forEach((s) => {
          if (s.exercise_type !== 'wim_hof') return;
          const arr = Array.isArray(s.retention_scores) ? s.retention_scores : [];
          arr.forEach((r) => {
            if (typeof r === 'number' && (!best || r > best.retention)) {
              best = { retention: r, date: s.completed_at };
            }
          });
        });
        setWimHofBest(best);
      } catch (e) {
        // Silent — display "aucune donnée" states
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // BOLT chart data (challenge_type = 'hold' historically).
  // Mobile view: if data spans more than 7 days, aggregate by ISO week (Mon–Sun)
  // and plot one point per week (the mean of all measurements that week).
  const [isMobileChart, setIsMobileChart] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const listener = (e) => setIsMobileChart(e.matches);
    mq.addEventListener?.('change', listener);
    return () => mq.removeEventListener?.('change', listener);
  }, []);

  const boltChart = useMemo(() => {
    const rows = challengeScores
      .filter((s) => s.challenge_type === 'hold' || s.challenge_type === 'bolt')
      .sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));

    // Full daily series (default view).
    const daily = rows.map((s) => {
      const d = new Date(s.completed_at);
      return {
        date: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        fullDate: d.toLocaleDateString('fr-FR'),
        value: s.duration_seconds,
      };
    });

    if (!isMobileChart || rows.length < 2) return daily;
    const spanDays =
      (new Date(rows[rows.length - 1].completed_at) - new Date(rows[0].completed_at)) /
      (1000 * 60 * 60 * 24);
    if (spanDays <= 7) return daily;

    // Aggregate by ISO week — key = Monday of that week (YYYY-MM-DD).
    const groups = new Map(); // key -> { sum, count, monday: Date }
    for (const s of rows) {
      const d = new Date(s.completed_at);
      const day = (d.getDay() + 6) % 7; // 0 = Monday … 6 = Sunday
      const monday = new Date(d);
      monday.setHours(0, 0, 0, 0);
      monday.setDate(d.getDate() - day);
      const key = monday.toISOString().slice(0, 10);
      const g = groups.get(key) || { sum: 0, count: 0, monday };
      g.sum += Number(s.duration_seconds) || 0;
      g.count += 1;
      groups.set(key, g);
    }
    return Array.from(groups.values())
      .sort((a, b) => a.monday - b.monday)
      .map((g) => {
        const sunday = new Date(g.monday);
        sunday.setDate(g.monday.getDate() + 6);
        return {
          date: `sem. ${g.monday.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}`,
          fullDate: `Semaine du ${g.monday.toLocaleDateString('fr-FR')} au ${sunday.toLocaleDateString('fr-FR')} · moyenne sur ${g.count} mesure${g.count > 1 ? 's' : ''}`,
          value: g.sum / g.count,
        };
      });
  }, [challengeScores, isMobileChart]);

  const setAnswer = (qid, val) => setAnswers((prev) => ({ ...prev, [qid]: val }));
  const resetSelfCheck = () => {
    setFeeling(null);
    setAnswers({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };
  // Program is now shown as soon as the user provides ANY input (feeling or one answer).
  // It grows progressively as they respond to more questions.
  const answeredCount = Object.keys(answers).length;
  const canShowProgram = Boolean(feeling) || answeredCount > 0;
  const program = useMemo(
    () => (canShowProgram ? buildProgram(feeling, answers) : null),
    [canShowProgram, feeling, answers]
  );

  // Rotating inspiring hero background — a different landscape on each visit.
  const HERO_BACKGROUNDS = useMemo(
    () => [
      // Curated calm landscapes (Unsplash CDN, safe-to-embed)
      'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=2000&q=70', // sea of clouds mountains
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2000&q=70', // misty forest sunrise
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=2000&q=70', // forest light rays
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=2000&q=70', // calm lake reflections
      'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=2000&q=70', // dawn mountains
      'https://images.unsplash.com/photo-1439066615861-d1af74d74000?auto=format&fit=crop&w=2000&q=70', // mountain river valley
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=2000&q=70', // aerial mountains clouds
      'https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?auto=format&fit=crop&w=2000&q=70', // pine forest sunlight
      'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=2000&q=70', // ocean sunset
      'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=2000&q=70', // starry sky mountain
    ],
    []
  );
  const heroBg = useMemo(
    () => HERO_BACKGROUNDS[Math.floor(Math.random() * HERO_BACKGROUNDS.length)],
    [HERO_BACKGROUNDS]
  );

  const fmtSecs = (s) => {
    if (!s && s !== 0) return '—';
    const n = Number(s);
    if (Number.isNaN(n)) return '—';
    if (n < 60) return `${Math.round(n)} s`;
    const m = Math.floor(n / 60);
    const r = Math.round(n % 60);
    return `${m} min ${r}s`;
  };
  const fmtDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // -----------------------------------------------------------------
  // "Mes Performances" — color-coded tiles based on published norms
  // -----------------------------------------------------------------
  // Each tile is scored on { value, unit } → returns
  //   status: 'green' | 'orange' | 'red' | 'unknown'
  //   advice: short recommendation string
  //
  // Norms:
  //   BOLT (rest hold)         : ≥40s green · 20–40s orange · <20s red
  //   MBT (walking pace count) : 40–80 pas green · 30–40 orange · <30 red
  //   Inspir/Expir max time    : ≥60s green · 45–60s orange · <45s red
  //   Apnée inspiratoire       : ≥60s green · 45–60s orange · <45s red
  //   Wim Hof best retention   : informative only (no norm)
  const performanceStatus = (key, value) => {
    if (value == null || Number.isNaN(Number(value))) return { status: 'unknown', advice: null };
    const v = Number(value);
    if (key === 'hold') {
      if (v >= 40) return { status: 'green', advice: 'Excellent BOLT ! Maintenez votre pratique quotidienne et affinez avec la Respiration Réduite.' };
      if (v >= 20) return { status: 'orange', advice: 'Tolérance au CO₂ à améliorer : pratiquez les Petites Apnées répétées (Buteyko) 10 min, 2×/jour.' };
      return { status: 'red', advice: 'Priorité : Petites Apnées répétées + Respiration Réduite pour élever progressivement votre tolérance au CO₂.' };
    }
    if (key === 'mbt') {
      if (v >= 40) return { status: 'green', advice: 'Très bonne tolérance à l\'effort ! Poursuivez le MBT régulier pour progresser encore.' };
      if (v >= 30) return { status: 'orange', advice: 'Tolérance modérée : combinez Respiration Réduite + BOLT quotidien pour améliorer votre MBT.' };
      return { status: 'red', advice: 'Endurance respiratoire faible : commencez par élever votre BOLT (Petites Apnées, Buteyko) avant de refaire le MBT.' };
    }
    if (key === 'inhale') {
      if (v >= 60) return { status: 'green', advice: 'Excellent contrôle inspiratoire ! Travaillez la douceur : viser une inspiration silencieuse.' };
      if (v >= 45) return { status: 'orange', advice: 'Presque dans la norme : entraînez le débit inspiratoire lent avec la Respiration Réduite.' };
      return { status: 'red', advice: 'Muscles inspiratoires à renforcer : pratiquez les étirements de cage thoracique et la Respiration Réduite quotidienne.' };
    }
    if (key === 'exhale') {
      if (v >= 60) return { status: 'green', advice: 'Excellent contrôle expiratoire ! Continuez à travailler l\'allongement du temps expiratoire.' };
      if (v >= 45) return { status: 'orange', advice: 'À améliorer : pratiquez "Focus sur la capacité à expirer" (allongement du temps expiratoire).' };
      return { status: 'red', advice: 'Priorité : "Focus sur la capacité à expirer" 10 min/jour + Lèvres pincées pour prolonger l\'expiration.' };
    }
    if (key === 'apnea') {
      if (v >= 60) return { status: 'green', advice: 'Excellente tolérance en apnée inspiratoire ! Maintenez avec la Respiration Carrée.' };
      if (v >= 45) return { status: 'orange', advice: 'Presque dans la norme : entraînez-vous avec la Respiration Carrée (4·4·4·4) puis (6·6·6·6).' };
      return { status: 'red', advice: 'À travailler : combinez Petites Apnées répétées + Respiration Carrée pour élever votre tolérance.' };
    }
    return { status: 'unknown', advice: null };
  };

  const STATUS_STYLE = {
    green:   { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0', badge: 'Dans la norme' },
    orange:  { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA', badge: 'À améliorer' },
    red:     { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA', badge: 'À travailler en priorité' },
    unknown: { bg: '#F8FAFC', text: '#475569', border: '#E2E8F0', badge: 'Non mesuré' },
  };

  const RECORD_TILES = [
    { key: 'hold',    label: 'BOLT (rétention repos)',   icon: Gauge,      extract: (r) => r?.hold },
    { key: 'mbt',     label: 'MBT (marche en apnée)',    icon: Footprints, extract: (r) => r?.mbt,   unit: 'pas' },
    { key: 'inhale',  label: 'Temps Inspiratoire Max',   icon: Wind,       extract: (r) => r?.inhale },
    { key: 'exhale',  label: 'Temps Expiratoire Max',    icon: Wind,       extract: (r) => r?.exhale },
    { key: 'apnea',   label: 'Apnée Inspiratoire',       icon: Timer,      extract: (r) => r?.apnea },
    { key: 'wim_hof', label: 'Wim Hof (meilleure rétention)', icon: Zap,   extract: () => wimHofBest ? { duration_seconds: wimHofBest.retention, completed_at: wimHofBest.date } : null },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.white, fontFamily: 'Figtree, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600;700&display=swap');
      `}</style>

      {!isPwa && <SiteHeaderV2 />}

      {/* PWA install prompt — mobile only, appears sticky above the fold */}
      {!isPwa && <InstallAppButton />}

      {/* HERO */}
      <section
        className="relative py-16 lg:py-20 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.55) 0%, rgba(2,132,199,0.35) 100%), url('${heroBg}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Discreet access to accessibility settings — fixed on the hero
            so it stays reachable regardless of scroll position. */}
        <Link
          to="/settings/accessibilite"
          className="absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-white/60 text-slate-700 text-xs font-medium shadow-sm hover:bg-white transition"
          data-testid="accessibility-link"
          title="Accessibilité"
        >
          <SettingsIcon size={14} />
          <span className="hidden sm:inline">Accessibilité</span>
        </Link>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.22em] mb-4"
            style={{ color: '#E0F2FE', fontWeight: 500, textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}
          >
            Mon espace
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#FFFFFF', textShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
          >
            Bonjour {user?.name?.split(' ')?.[0] || ''},{' '}
            <span style={{ color: '#7DD3FC', fontWeight: 400 }}>respirons ensemble.</span>
          </h1>
          <p
            className="text-lg leading-relaxed max-w-3xl"
            style={{ color: '#F1F5F9', textShadow: '0 1px 4px rgba(0,0,0,0.35)' }}
          >
            Retrouvez ici vos performances, faites le point sur votre état du moment, et
            obtenez un programme d’exercices personnalisé pour la séance du jour.
          </p>

          {/* Access to the patient's dedicated program page */}
          <div className="mt-8 flex justify-center sm:justify-start">
            <Link
              to="/mon-espace/mon-programme"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm sm:text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
              style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)' }}
              data-testid="hero-open-programme-btn"
              aria-label="Ouvrir mon programme"
            >
              <ClipboardList size={18} />
              <span>Mon programme</span>
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 1 — Mes performances */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="1">Records &amp; progression</SectionLabel>
          <SectionTitle>Mes performances</SectionTitle>

          {loading ? (
            <p className="text-sm" style={{ color: C.muted }}>Chargement de vos performances…</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {RECORD_TILES.map(({ key, label, icon: Icon, extract, unit }) => {
                  const r = extract(records) || null;
                  const rawValue = r ? (unit === 'pas' ? (r.steps ?? r.duration_seconds) : r.duration_seconds) : null;
                  const displayed = rawValue == null ? '—' : (unit === 'pas' ? `${rawValue} pas` : fmtSecs(rawValue));
                  const date = fmtDate(r?.completed_at || r?.date);
                  // Wim Hof has no norm — don't compute a status.
                  const { status, advice } = key === 'wim_hof'
                    ? { status: 'unknown', advice: null }
                    : performanceStatus(key, rawValue);
                  const style = STATUS_STYLE[status];
                  return (
                    <div
                      key={key}
                      className="rounded-2xl p-5 flex flex-col"
                      style={{
                        backgroundColor: C.white,
                        border: `1px solid ${status === 'unknown' ? C.border : style.border}`,
                        boxShadow: status !== 'unknown' ? `0 0 0 4px ${style.bg}` : 'none',
                      }}
                      data-testid={`record-tile-${key}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: style.bg, color: style.text }}
                        >
                          <Icon size={20} />
                        </div>
                        <p className="text-sm font-medium flex-1" style={{ color: C.slate }}>{label}</p>
                      </div>
                      <p
                        className="text-3xl mb-1"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 400,
                          color: rawValue == null ? C.ink : style.text,
                        }}
                      >
                        {displayed}
                      </p>
                      {rawValue != null && status !== 'unknown' && (
                        <span
                          className="inline-block self-start text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2"
                          style={{ backgroundColor: style.bg, color: style.text }}
                          data-testid={`record-status-${key}`}
                        >
                          {style.badge}
                        </span>
                      )}
                      {date && (
                        <p className="text-xs mb-2" style={{ color: C.muted }}>Enregistré le {date}</p>
                      )}
                      {advice && (
                        <p
                          className="text-xs mt-auto pt-2 border-t"
                          style={{ color: C.slate, borderColor: C.border }}
                          data-testid={`record-advice-${key}`}
                        >
                          <strong style={{ color: style.text }}>À travailler :</strong> {advice}
                        </p>
                      )}
                      {!advice && rawValue == null && (
                        <p className="text-xs mt-auto pt-2 border-t italic" style={{ color: C.muted, borderColor: C.border }}>
                          Mesurez-le pour obtenir un score personnalisé.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* BOLT progression chart */}
              <div className="mt-10">
                <h3
                  className="text-xl mb-1"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
                >
                  Progression de mon score BOLT
                </h3>
                {isMobileChart && boltChart.length >= 2 && boltChart[0]?.date?.startsWith?.('sem.') && (
                  <p className="text-xs mb-3" style={{ color: C.muted }} data-testid="bolt-chart-weekly-hint">
                    Vue mobile : une moyenne par semaine (Lun–Dim). Passez sur un point pour voir le détail.
                  </p>
                )}
                {boltChart.length < 2 ? (
                  <div
                    className="rounded-2xl p-6 text-center"
                    style={{ backgroundColor: C.cloud, border: `1px dashed ${C.border}` }}
                  >
                    <p className="text-sm" style={{ color: C.muted }}>
                      Enregistrez au moins 2 mesures de BOLT pour visualiser votre progression.
                    </p>
                    <Link
                      to="/breathing/challenges-intro"
                      className="inline-flex items-center gap-2 mt-3 text-sm font-medium"
                      style={{ color: C.primaryDark }}
                      data-testid="cta-measure-bolt"
                    >
                      Mesurer mon BOLT maintenant <ChevronRight size={14} />
                    </Link>
                  </div>
                ) : (
                  <div
                    className="rounded-2xl p-4"
                    style={{ backgroundColor: C.white, border: `1px solid ${C.border}`, height: 260 }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={boltChart} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: C.muted }} />
                        <YAxis tick={{ fontSize: 11, fill: C.muted }} />
                        <Tooltip
                          content={({ active, payload }) =>
                            active && payload && payload.length ? (
                              <div className="bg-white p-2 rounded-lg shadow border" style={{ borderColor: C.border }}>
                                <p className="text-xs" style={{ color: C.muted }}>{payload[0].payload.fullDate}</p>
                                <p className="font-semibold" style={{ color: C.primaryDark }}>
                                  {Number(payload[0].value).toFixed(1)} s
                                </p>
                              </div>
                            ) : null
                          }
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={C.primary}
                          strokeWidth={2.5}
                          dot={{ r: 4, fill: C.primary }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* SECTION 2 — Feeling */}
      <section className="py-14" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="2">Point de départ</SectionLabel>
          <SectionTitle>Comment vous sentez-vous aujourd’hui ?</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
            {FEELINGS.map((f) => {
              const active = feeling === f.id;
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFeeling(f.id)}
                  className="rounded-2xl p-4 text-center transition-all hover:-translate-y-1"
                  style={{
                    backgroundColor: active ? C.primary : C.white,
                    color: active ? C.white : C.ink,
                    border: `1px solid ${active ? C.primary : C.border}`,
                    boxShadow: active ? '0 8px 20px rgba(14,165,233,0.25)' : 'none',
                  }}
                  data-testid={`feeling-${f.id}`}
                >
                  <div
                    className="w-11 h-11 mx-auto rounded-full flex items-center justify-center mb-3"
                    style={{
                      backgroundColor: active ? 'rgba(255,255,255,0.15)' : C.skySoft,
                      color: active ? C.white : f.color,
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <p className="text-sm font-medium leading-tight">{f.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — Self-check questionnaire */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="3">Auto-observation</SectionLabel>
          <SectionTitle>Comment ce corps respire-t-il en ce moment ?</SectionTitle>
          <p className="text-base leading-relaxed mb-8" style={{ color: C.slate }}>
            Prenez un instant pour observer votre respiration telle qu’elle est,
            sans la modifier. Répondez à ces 10 questions : votre programme personnalisé
            apparaîtra ci-dessous.
          </p>
          <div className="space-y-4">
            {CHECK_QUESTIONS.map((q, idx) => (
              <div
                key={q.id}
                className="rounded-xl p-4 sm:p-5"
                style={{
                  backgroundColor: C.white,
                  border: `1px solid ${answers[q.id] ? C.skySoft : C.border}`,
                }}
                data-testid={`selfcheck-${q.id}`}
              >
                <p className="text-sm font-medium mb-3" style={{ color: C.ink }}>
                  {idx + 1}. {q.prompt}
                </p>
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => {
                    const active = answers[q.id] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAnswer(q.id, opt.value)}
                        className="text-xs px-3 py-2 rounded-full transition-all"
                        style={{
                          backgroundColor: active ? C.primary : C.cloud,
                          color: active ? C.white : C.slate,
                          border: `1px solid ${active ? C.primary : C.border}`,
                          fontWeight: active ? 600 : 400,
                        }}
                        data-testid={`selfcheck-${q.id}-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={resetSelfCheck}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: C.white, color: C.primaryDark, border: `1px solid ${C.border}` }}
              data-testid="reset-selfcheck"
            >
              <RefreshCcw size={14} />
              Recommencer
            </button>
            <p className="text-xs" style={{ color: C.muted }}>
              {Object.keys(answers).length} / {CHECK_QUESTIONS.length} questions répondues
              {feeling ? ' · humeur sélectionnée' : ' · humeur à sélectionner'}
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Personalized program */}
      <section id="programme" className="py-14" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-5xl mx-auto px-6">
          <SectionLabel number="4">Votre séance du jour</SectionLabel>
          <SectionTitle>Programme personnalisé</SectionTitle>

          {!canShowProgram && (
            <div
              className="rounded-2xl p-6 text-center"
              style={{ backgroundColor: C.white, border: `1px dashed ${C.border}` }}
              data-testid="program-empty"
            >
              <Sparkles size={22} className="mx-auto mb-3" style={{ color: C.primaryDark }} />
              <p className="text-sm" style={{ color: C.slate }}>
                Sélectionnez votre humeur ou répondez à au moins une question d’auto-observation
                pour voir votre programme se composer en temps réel.
              </p>
            </div>
          )}

          {canShowProgram && program && (
            <div className="space-y-6">
              {/* Today's progress badge */}
              {(program.exercises.length > 0 || program.videos.length > 0) && (
                <div
                  className="rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap"
                  style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
                  data-testid="today-progress"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: done.exercises.length + done.videos.length > 0 ? '#D1FAE5' : C.skySoft,
                        color: done.exercises.length + done.videos.length > 0 ? '#065F46' : C.primaryDark,
                      }}
                    >
                      <CheckCircle2 size={18} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.primaryDark, fontWeight: 500 }}>Progression du jour</p>
                      <p className="text-sm font-semibold" style={{ color: C.ink }}>
                        {done.exercises.length} / {program.exercises.length} exercice{program.exercises.length > 1 ? 's' : ''}
                        {program.videos.length > 0 && ` · ${done.videos.length} / ${program.videos.length} vidéo${program.videos.length > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={sendSummaryToCabinet}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors"
                    style={{ backgroundColor: C.primary, color: C.white, border: `1px solid ${C.primary}` }}
                    data-testid="send-summary-cabinet"
                  >
                    Envoyer ce bilan au cabinet
                  </button>
                </div>
              )}

              {answeredCount < CHECK_QUESTIONS.length && (
                <p
                  className="text-xs italic"
                  style={{ color: C.muted }}
                  data-testid="program-progressive-hint"
                >
                  Programme mis à jour à chaque réponse — {CHECK_QUESTIONS.length - answeredCount} question{CHECK_QUESTIONS.length - answeredCount > 1 ? 's' : ''} restante{CHECK_QUESTIONS.length - answeredCount > 1 ? 's' : ''} pour l’affiner.
                </p>
              )}
              {/* Priority alert if air hunger */}
              {answers.air_hunger === 'yes' && (
                <div
                  className="rounded-2xl p-5 sm:p-6 flex gap-4"
                  style={{ backgroundColor: C.amberBg, border: `1px solid ${C.amberBorder}` }}
                  data-testid="program-air-hunger"
                >
                  <AlertTriangle size={22} style={{ color: C.amber, flexShrink: 0 }} />
                  <div>
                    <p className="font-semibold mb-1" style={{ color: C.amber }}>
                      Priorité : sensation de manque d’air
                    </p>
                    <p className="text-sm" style={{ color: C.ink }}>
                      Commencez par <strong>mesurer votre BOLT</strong> pour poser un point
                      de référence, puis pratiquez immédiatement les{' '}
                      <strong>petites apnées répétées</strong>. Les autres exercices peuvent
                      être ajoutés ensuite.
                    </p>
                  </div>
                </div>
              )}

              {/* Explanations */}
              {program.reasons.length > 0 && (
                <div
                  className="rounded-2xl p-5 sm:p-6"
                  style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
                  data-testid="program-reasons"
                >
                  <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: C.primaryDark, fontWeight: 500 }}>
                    Pourquoi ce programme
                  </p>
                  <ul className="space-y-2">
                    {program.reasons.map((r) => (
                      <li key={r.key} className="flex gap-3 text-sm leading-relaxed" style={{ color: C.slate }}>
                        <span
                          className="mt-2 rounded-full flex-shrink-0"
                          style={{ width: 5, height: 5, backgroundColor: r.critical ? C.amber : C.primary }}
                        />
                        <span>{r.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Coaching blocks — step-by-step protocols */}
              {program.blocks && program.blocks.length > 0 && (
                <div className="space-y-4" data-testid="program-blocks">
                  {program.blocks.map((b) => (
                    <div
                      key={b.id}
                      className="rounded-2xl p-5 sm:p-6"
                      style={{ backgroundColor: C.white, border: `1px solid ${C.skySoft}` }}
                      data-testid={`coaching-block-${b.id}`}
                    >
                      <p
                        className="text-xs uppercase tracking-[0.2em] mb-2"
                        style={{ color: C.primaryDark, fontWeight: 500 }}
                      >
                        Protocole guidé
                      </p>
                      <h4
                        className="text-xl mb-3"
                        style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
                      >
                        {b.title}
                      </h4>
                      {b.intro && (
                        <p className="text-sm leading-relaxed mb-4" style={{ color: C.slate }}>
                          {b.intro}
                        </p>
                      )}

                      {b.ifEasyLabel && (
                        <div
                          className="rounded-xl p-4 mb-3"
                          style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }}
                        >
                          <p className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: '#059669', fontWeight: 600 }}>
                            ✓ {b.ifEasyLabel}
                          </p>
                          <p className="text-sm" style={{ color: C.ink }}>{b.ifEasyText}</p>
                        </div>
                      )}

                      {b.ifHardLabel && (
                        <div
                          className="rounded-xl p-4"
                          style={{ backgroundColor: C.skyPale, border: `1px solid ${C.skySoft}` }}
                        >
                          <p className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: C.primaryDark, fontWeight: 600 }}>
                            → {b.ifHardLabel}
                          </p>
                          {b.ifHardIntro && (
                            <p className="text-sm mb-3" style={{ color: C.slate }}>{b.ifHardIntro}</p>
                          )}
                          <ol className="space-y-3">
                            {(b.ifHardSteps || []).map((s, i) => (
                              <li key={i} className="flex gap-3 text-sm">
                                <span
                                  className="flex-shrink-0 flex items-center justify-center text-xs font-semibold"
                                  style={{
                                    width: 22, height: 22, borderRadius: '50%',
                                    backgroundColor: C.primary, color: C.white,
                                  }}
                                >
                                  {i + 1}
                                </span>
                                <div>
                                  <p className="font-semibold" style={{ color: C.ink }}>{s.title}</p>
                                  <p className="leading-relaxed" style={{ color: C.slate }}>{s.body}</p>
                                </div>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {b.steps && (
                        <ol className="space-y-3">
                          {b.steps.map((s, i) => (
                            <li key={i} className="flex gap-3 text-sm">
                              <span
                                className="flex-shrink-0 flex items-center justify-center text-xs font-semibold"
                                style={{
                                  width: 22, height: 22, borderRadius: '50%',
                                  backgroundColor: C.primary, color: C.white,
                                }}
                              >
                                {i + 1}
                              </span>
                              <div>
                                <p className="font-semibold" style={{ color: C.ink }}>{s.title}</p>
                                <p className="leading-relaxed" style={{ color: C.slate }}>{s.body}</p>
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Exercise cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {program.exercises.map((ex, i) => {
                  const Icon = ex.icon || Sparkles;
                  const doneNow = isDoneExercise(ex.key);
                  return (
                    <div
                      key={ex.key}
                      className="relative rounded-2xl p-5 transition-all"
                      style={{
                        backgroundColor: ex.priority ? C.amberBg : C.white,
                        border: `1px solid ${doneNow ? '#10B981' : (ex.priority ? C.amberBorder : C.border)}`,
                        opacity: doneNow ? 0.7 : 1,
                      }}
                      data-testid={`program-exercise-${ex.key}`}
                    >
                      <Link to={ex.href} className="block group">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-11 h-11 rounded-lg flex items-center justify-center"
                              style={{
                                backgroundColor: ex.priority ? C.white : C.skySoft,
                                color: ex.priority ? C.amber : C.primaryDark,
                              }}
                            >
                              <Icon size={20} />
                            </div>
                            <div>
                              {ex.priority && (
                                <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.amber, fontWeight: 600 }}>
                                  Priorité {i + 1}
                                </p>
                              )}
                              <p className="font-semibold" style={{ color: C.ink }}>{ex.title}</p>
                            </div>
                          </div>
                          <Play size={18} className="opacity-40 group-hover:opacity-100" style={{ color: C.primaryDark }} />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: C.slate }}>{ex.desc}</p>
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); toggleDoneExercise(ex.key); }}
                        className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                        style={{
                          backgroundColor: doneNow ? '#D1FAE5' : C.cloud,
                          color: doneNow ? '#065F46' : C.primaryDark,
                          border: `1px solid ${doneNow ? '#10B981' : C.border}`,
                        }}
                        data-testid={`toggle-done-exercise-${ex.key}`}
                      >
                        {doneNow ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                        {doneNow ? 'Pratiqué' : 'Marquer fait'}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Educational YouTube videos — mechanical aspects */}
              {program.videos && program.videos.length > 0 && (
                <div
                  className="rounded-2xl p-5 sm:p-6"
                  style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
                  data-testid="program-videos"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                    >
                      <Youtube size={20} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em]" style={{ color: C.primaryDark, fontWeight: 500 }}>
                        À visionner
                      </p>
                      <h4 className="font-semibold" style={{ color: C.ink }}>
                        Vidéos explicatives (mécanique)
                      </h4>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {program.videos.map(({ key, video, reason }) => {
                      const doneV = isDoneVideo(key);
                      return (
                        <div
                          key={key}
                          className="relative rounded-xl overflow-hidden"
                          style={{
                            border: `1px solid ${doneV ? '#10B981' : C.border}`,
                            backgroundColor: C.cloud,
                            opacity: doneV ? 0.75 : 1,
                          }}
                        >
                          <a
                            href={ytUrl(video.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group"
                            data-testid={`program-video-${key}`}
                          >
                            <div className="relative aspect-video overflow-hidden">
                              <img src={ytThumb(video.id)} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
                              <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)' }}>
                                <div className="w-12 h-12 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: '#DC2626' }}>
                                  <Play size={20} className="text-white ml-0.5" fill="white" />
                                </div>
                              </div>
                              <span className="absolute bottom-2 right-2 text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.75)', color: 'white' }}>{video.duration}</span>
                            </div>
                            <div className="p-4 pb-2">
                              <p className="text-sm font-medium leading-snug mb-1" style={{ color: C.ink }}>{video.title}</p>
                              <p className="text-xs leading-relaxed" style={{ color: C.slate }}>{reason}</p>
                            </div>
                          </a>
                          <div className="px-4 pb-3">
                            <button
                              type="button"
                              onClick={() => toggleDoneVideo(key)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: doneV ? '#D1FAE5' : C.white,
                                color: doneV ? '#065F46' : C.primaryDark,
                                border: `1px solid ${doneV ? '#10B981' : C.border}`,
                              }}
                              data-testid={`toggle-done-video-${key}`}
                            >
                              {doneV ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                              {doneV ? 'Regardé' : 'Marquer regardé'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 text-right">
                    <a
                      href="https://www.youtube.com/@Jaccardjphysio"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium"
                      style={{ color: C.primaryDark }}
                      data-testid="program-videos-channel"
                    >
                      Voir toute la chaîne <ChevronRight size={14} />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 4.5 — History of check-ins */}
      {history.length > 0 && (
        <section id="historique" className="py-14">
          <div className="max-w-4xl mx-auto px-6">
            <SectionLabel>Journal</SectionLabel>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <SectionTitle>Historique de mes bilans</SectionTitle>
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{ backgroundColor: C.white, color: C.primaryDark, border: `1px solid ${C.border}` }}
                data-testid="toggle-history"
              >
                <History size={14} />
                {historyOpen ? 'Masquer' : `Voir (${history.length})`}
              </button>
            </div>
            {historyOpen && (
              <div className="space-y-3" data-testid="history-list">
                {history.map((h) => {
                  const feelingLabel = FEELINGS.find((f) => f.id === h.feeling)?.label || '—';
                  const dateFmt = new Date(h.date + 'T00:00:00').toLocaleDateString('fr-FR', {
                    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
                  });
                  const nbAnswered = h.answers ? Object.keys(h.answers).length : 0;
                  return (
                    <div
                      key={h.date}
                      className="rounded-xl p-4 flex flex-wrap items-center justify-between gap-3"
                      style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
                    >
                      <div>
                        <p className="text-sm font-semibold capitalize" style={{ color: C.ink }}>{dateFmt}</p>
                        <p className="text-xs" style={{ color: C.slate }}>
                          Humeur : <strong>{feelingLabel}</strong> · {nbAnswered} question{nbAnswered > 1 ? 's' : ''} répondue{nbAnswered > 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: C.skyPale, color: C.primaryDark }}>
                          {h.exercisesDone || 0} exercice{(h.exercisesDone || 0) > 1 ? 's' : ''}
                        </span>
                        <span className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: C.skyPale, color: C.primaryDark }}>
                          {h.videosDone || 0} vidéo{(h.videosDone || 0) > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* SECTION 5 — Daily science-backed tips */}
      <section id="conseils" className="py-14">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="5">Hygiène de vie</SectionLabel>
          <SectionTitle>Conseils quotidiens fondés sur les preuves</SectionTitle>
          <p className="text-base leading-relaxed mb-8 max-w-3xl" style={{ color: C.slate }}>
            Une respiration saine ne se travaille pas qu’en séance. Voici les leviers
            du quotidien qui la soutiennent, avec les recommandations issues des données
            scientifiques les plus récentes.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Sommeil */}
            <article
              className="rounded-2xl p-6 h-full"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              data-testid="tip-sleep"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}
                >
                  <Moon size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.primaryDark, fontWeight: 500 }}>
                    Fondement
                  </p>
                  <h3 className="font-semibold" style={{ color: C.ink }}>Sommeil</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.slate }}>
                Les consensus de l’American Academy of Sleep Medicine et de la Sleep
                Research Society recommandent <strong>7 heures ou plus par nuit</strong>
                {' '}pour l’adulte (18-60 ans). Une durée de <strong>7-8 h</strong> est
                associée aux meilleurs profils de santé (relation en U).
              </p>
              <ul className="space-y-1.5 text-sm mb-3" style={{ color: C.slate }}>
                <li>• Heures de coucher et de lever régulières (± 30 min).</li>
                <li>• Chambre fraîche (16-19 °C), sombre, sans écran.</li>
                <li>• Pas de caféine après 14 h ; alcool évité en soirée.</li>
                <li>• Insomnie ? Une thérapie <strong>CBT-I</strong> (ou restriction de sommeil, activité physique) est plus efficace que la seule « hygiène de sommeil ».</li>
              </ul>
              <p className="text-xs" style={{ color: C.muted }}>
                Sources :{' '}
                <a href="https://aasm.org/seven-or-more-hours-of-sleep-per-night-a-health-necessity-for-adults/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>AASM/SRS</a>{' '}·{' '}
                <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11152306/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>PLOS One 2024 (méta-analyse)</a>
              </p>
            </article>

            {/* Alimentation */}
            <article
              className="rounded-2xl p-6 h-full"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              data-testid="tip-nutrition"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#ECFDF5', color: '#059669' }}
                >
                  <Utensils size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.primaryDark, fontWeight: 500 }}>
                    Base
                  </p>
                  <h3 className="font-semibold" style={{ color: C.ink }}>Alimentation</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.slate }}>
                Le régime <strong>méditerranéen</strong> reste le plus documenté pour la
                santé cardiovasculaire, cognitive et respiratoire. L’OMS recommande{' '}
                <strong>&ge; 400 g de fruits et légumes/jour</strong>, moins de{' '}
                <strong>5 g de sel</strong> et moins de <strong>10 % des apports en
                sucres libres</strong>.
              </p>
              <ul className="space-y-1.5 text-sm mb-3" style={{ color: C.slate }}>
                <li>• Priorité aux végétaux, légumineuses, céréales complètes.</li>
                <li>• Poissons gras 1-2 fois/semaine (oméga-3), huile d’olive vierge.</li>
                <li>• Ultra-transformés et sucres libres au minimum.</li>
                <li>• Hydratation : 1,5-2 L d’eau/jour (plus si chaleur / effort).</li>
              </ul>
              <p className="text-xs" style={{ color: C.muted }}>
                Sources :{' '}
                <a href="https://www.who.int/news-room/fact-sheets/detail/healthy-diet" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>OMS – Healthy diet</a>{' '}·{' '}
                <a href="https://www.efsa.europa.eu/en/topics/topic/dietary-reference-values" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>EFSA – DRV</a>
              </p>
            </article>

            {/* Magnésium */}
            <article
              className="rounded-2xl p-6 h-full"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              data-testid="tip-magnesium"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                >
                  <Pill size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.primaryDark, fontWeight: 500 }}>
                    Minéral
                  </p>
                  <h3 className="font-semibold" style={{ color: C.ink }}>Magnésium</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.slate }}>
                Apport de référence EFSA : <strong>300 mg/j (femme)</strong>,{' '}
                <strong>350 mg/j (homme)</strong>. Les revues 2024 suggèrent une amélioration
                modeste du <strong>sommeil</strong> (5/8 études) et de l’<strong>anxiété</strong>{' '}
                {' '}(5/7 études), avec des posologies de 320-729 mg/j en <em>supplémentation</em>.
                Effets secondaires : diarrhée si dose trop élevée.
              </p>
              <ul className="space-y-1.5 text-sm mb-3" style={{ color: C.slate }}>
                <li>• Sources alimentaires : oléagineux, légumineuses, chocolat noir, céréales complètes, eaux minérales riches en Mg.</li>
                <li>• Formes mieux tolérées si supplémentation : bisglycinate, citrate.</li>
                <li>• Avis médical requis en cas d’insuffisance rénale ou de traitement en cours.</li>
              </ul>
              <p className="text-xs" style={{ color: C.muted }}>
                Sources :{' '}
                <a href="https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2015.4186" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>EFSA 2015 (DRV magnésium)</a>{' '}·{' '}
                <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8053283/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>PMC 2021 (sommeil)</a>{' '}·{' '}
                <a href="https://assets.cureus.com/uploads/review_article/pdf/237565/20240530-26594-143i0co.pdf" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>Revue Cureus 2024</a>
              </p>
            </article>

            {/* Zinc */}
            <article
              className="rounded-2xl p-6 h-full"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              data-testid="tip-zinc"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#F3E8FF', color: '#7C3AED' }}
                >
                  <Pill size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.primaryDark, fontWeight: 500 }}>
                    Minéral
                  </p>
                  <h3 className="font-semibold" style={{ color: C.ink }}>Zinc</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.slate }}>
                Apport de référence EFSA : <strong>~7 à 10 mg/j</strong> pour l’adulte
                selon la biodisponibilité (plus si régime riche en phytates). Rôle clé
                dans l’<strong>immunité</strong>, la cicatrisation et la santé des
                muqueuses respiratoires. Cochrane : bénéfice modeste sur la <em>durée</em>
                {' '}du rhume banal, effets indésirables digestifs possibles.
              </p>
              <ul className="space-y-1.5 text-sm mb-3" style={{ color: C.slate }}>
                <li>• Sources : viande, huîtres, œufs, légumineuses, graines de courge.</li>
                <li>• Une supplémentation prolongée &gt; 25 mg/j peut perturber l’absorption du cuivre.</li>
                <li>• Éviter l’auto-supplémentation sans indication médicale.</li>
              </ul>
              <p className="text-xs" style={{ color: C.muted }}>
                Sources :{' '}
                <a href="https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2014.3844" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>EFSA 2014 (DRV zinc)</a>{' '}·{' '}
                <a href="https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD014914/full" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>Cochrane 2024 (rhume)</a>
              </p>
            </article>

            {/* Potassium */}
            <article
              className="rounded-2xl p-6 h-full md:col-span-2"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              data-testid="tip-potassium"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                >
                  <Droplet size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: C.primaryDark, fontWeight: 500 }}>
                    Minéral
                  </p>
                  <h3 className="font-semibold" style={{ color: C.ink }}>Potassium</h3>
                </div>
              </div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.slate }}>
                L’<strong>OMS</strong> et l’<strong>EFSA</strong> recommandent{' '}
                <strong>3 500 mg/j (90 mmol)</strong> pour l’adulte (4 000 mg/j pour
                la femme allaitante). L’augmentation de l’apport en potassium
                <em> alimentaire</em> réduit la <strong>pression artérielle</strong> et le
                risque d’<strong>AVC</strong>, particulièrement chez les personnes à
                apport élevé en sel.
              </p>
              <ul className="space-y-1.5 text-sm mb-3" style={{ color: C.slate }}>
                <li>• Sources : légumes verts, banane, pomme de terre, légumineuses, fruits secs (abricot, dattes).</li>
                <li>• L’apport <em>par l’alimentation</em> est privilégié — la supplémentation demande un avis médical (risque d’hyperkaliémie chez l’insuffisant rénal ou sous certains médicaments comme les IEC/ARA-II, diurétiques épargneurs de potassium).</li>
                <li>• Combiner : plus de potassium + moins de sodium = effet maximal sur la tension.</li>
              </ul>
              <p className="text-xs" style={{ color: C.muted }}>
                Sources :{' '}
                <a href="https://www.who.int/publications/i/item/9789241504829" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>OMS 2012 (potassium)</a>{' '}·{' '}
                <a href="https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2016.4592" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.primaryDark }}>EFSA 2016 (AI potassium)</a>
              </p>
            </article>
          </div>

          {/* Global disclaimer */}
          <div
            className="mt-8 rounded-2xl p-5 flex gap-4"
            style={{ backgroundColor: C.skyPale, border: `1px solid ${C.skySoft}` }}
          >
            <BookOpen size={22} style={{ color: C.primaryDark, flexShrink: 0, marginTop: 2 }} />
            <div className="text-sm leading-relaxed" style={{ color: C.ink }}>
              <p className="font-semibold mb-1" style={{ color: C.primaryDark }}>
                Note importante
              </p>
              <p style={{ color: C.slate }}>
                Ces conseils synthétisent les recommandations d’organismes officiels
                (OMS, EFSA, AASM) et de méta-analyses récentes.{' '}
                <strong>Ils ne remplacent pas un avis médical personnalisé</strong>,
                notamment en cas de pathologie chronique, de grossesse, de traitement au
                long cours ou avant toute supplémentation.
              </p>
              <a
                href="https://www.who.int/health-topics"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-sm font-medium"
                style={{ color: C.primaryDark }}
              >
                Consulter les recommandations OMS <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {isPwa ? (
        <>
          {/* App-mode padding for the fixed bottom nav + mobile brand strip */}
          <div style={{ height: 'calc(env(safe-area-inset-bottom,0px) + 130px)' }} aria-hidden="true" />
          <Navigation />
        </>
      ) : (
        <SiteFooterV2 />
      )}
    </div>
  );
};

export default MonEspacePage;